import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { AccountSelectionService, OrganizationAccountGroupService } from "@front/m19-services";
import { IBadgeComponent, ISelectMenuImports } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { UntilDestroy } from "@ngneat/until-destroy";
import { OrganizationAccountGroups } from "@front/m19-models";
import { AccessLevel } from "@front/m19-api-client";
import { toSignal } from "@angular/core/rxjs-interop";

@UntilDestroy()
@Component({
  selector: "app-organization-selector",
  imports: [TranslocoDirective, ISelectMenuImports, IBadgeComponent],
  templateUrl: "./organization-selector.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationSelectorComponent {
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly organizationAccountGroupService = inject(OrganizationAccountGroupService);

  protected readonly AccessLevel = AccessLevel;
  private organizationsAccountGroups = toSignal(this.organizationAccountGroupService.allOrganizationAccountGroups$, {
    requireSync: true,
  });

  private selectedAccountGroups = toSignal(this.accountSelectionService.accountGroupSelection$, { requireSync: true });

  protected readonly searchQuery = signal("");

  protected readonly organizations = computed(() => {
    const orgs = [...(this.organizationsAccountGroups() ?? [])];
    orgs.sort((a, b) =>
      a.dataWarehouseAccess == b.dataWarehouseAccess ? a.id - b.id : a.dataWarehouseAccess ? -1 : 1,
    );
    return orgs.filter(
      (organizationAccountGroups: OrganizationAccountGroups) => organizationAccountGroups.accountGroups.length > 0,
    );
  });

  protected readonly filteredOrganizations = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const orgs = this.organizations();
    if (!query) return orgs;
    return orgs.filter((org) => org.organizationName.toLowerCase().includes(query));
  });

  protected readonly selectedOrganization = computed(() => {
    return this.organizationsAccountGroups().find((org) => org.id === this.selectedAccountGroups().organizationId);
  });

  selectOrganization(organization: OrganizationAccountGroups | undefined) {
    if (!organization) return;
    this.accountSelectionService.selectAccountGroups(organization.accountGroups);
  }
}
